import getPermissionsApi from '@/api/get/config/get-permissions';
import Card from '@/components/card/card-component';
import CircleIndicator from '@/components/circle-indicator/circle-indicator-component';
import Loader from '@/components/loader/loader-component';
import { Col, Row } from '@/components/grid/grid-component';
import Checkbox from '@/components/react-hook-form-inputs/checkbox/checkbox-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import { useQuery } from '@/hooks/use-api';
import { Heading3Text, ViewSmallTitleText } from '@/utils/global-fonts';
import {
  requiredValidationDropdownMessage,
  requiredValidationMessage,
} from '@/utils/validations';
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Styled from './user-role-style';

const styledCheckboxComponent = {
  paddingLeft: '8px',
  display: 'flex',
  alignItems: 'center',
};
const styledCheckboxCol = { margin: 0 };

const renderCheckBoxes = (changePermission, options, featureName, index) => {
  if (options) {
    const option = { ...options };

    delete option.feature_id;
    delete option.feature;
    const cleanedOption = {
      all: false,
      ...option,
    };

    const keys = Object.keys(cleanedOption);

    return keys.map((key) => (
      <Styled.CheckBoxContainer key={`cb-${featureName}-${key}`}>
        <Checkbox
          defaultValue={false}
          style={styledCheckboxComponent}
          cy={`${featureName}_${key[0].toUpperCase()}${key.slice(1)}`}
          type="checkbox"
          label={key[0].toUpperCase() + key.slice(1)}
          name={`permissions.${index}.${key}`}
          onClick={(e) => {
            changePermission(e.target.checked, index, key);
          }}
        />
      </Styled.CheckBoxContainer>
    ));
  }
};

/**
 *
 * @param {object} props - props
 * @param {string} props.type - props type
 * @returns {JSX.Element} UserRoleForm
 */
const UserRolesForm = ({ type }) => {
  const [userRoleStatus, setUserRoleStatus] = useState('');
  const { getValues, setValue } = useFormContext();
  const { data: permissions, loading } = useQuery(getPermissionsApi);

  const changePermission = (value, index, permissionType) => {
    const ALL = getValues(`permissions.${index}.all`);
    const CREATE =
      permissionType === 'create'
        ? value
        : getValues(`permissions.${index}.create`);
    const UPDATE =
      permissionType === 'update'
        ? value
        : getValues(`permissions.${index}.update`);
    const DELETE =
      permissionType === 'delete'
        ? value
        : getValues(`permissions.${index}.delete`);
    const VIEW =
      permissionType === 'read'
        ? value
        : getValues(`permissions.${index}.read`);
    let changeAllValue = false;

    if (permissionType === 'all') {
      if (permissions.length && permissions[index]) {
        const values = {};
        const keys = Object.keys(permissions[index]);
        keys.forEach((key) => {
          values[key] = value;
        });

        setValue(`permissions.${index}`, values);
      } else {
        setValue(`permissions.${index}`, {
          all: value,
          create: value,
          update: value,
          delete: value,
          read: value,
        });
      }
      changeAllValue = true;
    }
    if (!value && ALL) {
      setValue(`permissions.${index}.all`, false);
      changeAllValue = true;
    }
    if (!changeAllValue) {
      if (!ALL && CREATE && UPDATE && DELETE && VIEW) {
        setValue(`permissions.${index}.all`, true);
      }
    }

    if ((CREATE || UPDATE || DELETE) && !VIEW) {
      setValue(`permissions.${index}.read`, true);
    }
  };

  const userRoleStatusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'new', label: 'New' },
  ];

  if (loading) {
    return (
      <Card height="100%" padding="16px 18px 10px 24px" width="100%">
        <Row direction="row">
          <Col xs={12}>
            <Loader loading />
          </Col>
        </Row>
      </Card>
    );
  }
  return (
    <>
      <Card padding="16px 18px 10px 24px" width="100%">
        <Row direction="row">
          <Col xs={12}>
            <Heading3Text>
              {type === 'create' ? 'Create User Role' : 'Edit User Role'}
            </Heading3Text>
          </Col>
        </Row>
        <Row margin="0 0 0 0">
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <Input
              disabled={loading}
              label="User Role Name"
              placeholder="Role Name"
              name="name"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
          <Col sm={12} md={12} lg={6} xl={6} xxl={6}>
            <SelectSearch
              cy="selUserRoleStatus"
              header="Status"
              name="status"
              label="Please select a status"
              options={userRoleStatusOptions}
              icon={<CircleIndicator status={userRoleStatus} />}
              required={{
                required: requiredValidationDropdownMessage,
                onChange: (e) => {
                  setUserRoleStatus(e.target.value);
                },
              }}
              isLoading={loading}
            />
          </Col>
        </Row>
        <Row margin="0 0 0 0">
          <Col sm={12} md={12} lg={12} xl={12} xxl={12}>
            <Input
              disabled={loading}
              label="Description"
              placeholder="Role description"
              name="description"
              registerOptions={{
                required: requiredValidationMessage,
              }}
            />
          </Col>
        </Row>
      </Card>
      <Card padding="16px 18px 10px 24px" margin="24px 0 0 0" width="100%">
        <Row direction="row">
          <Col xs={12}>
            <Heading3Text>User Role Permissions</Heading3Text>
          </Col>
        </Row>
        {permissions?.map((perm) => (
          <Fragment key={`permission-${perm.feature_id ?? perm.id}`}>
            <Row>
              <Col style={styledCheckboxCol} xs={12}>
                <ViewSmallTitleText
                  color="#777777"
                  $toUpper
                  data-cy={`h4${perm.feature.name}`}
                >
                  {perm.feature.name}
                </ViewSmallTitleText>
              </Col>
              <Styled.CheckboxColStyle xs={12}>
                {renderCheckBoxes(
                  changePermission,
                  perm,
                  perm.feature.name,
                  perm.feature_id,
                )}
              </Styled.CheckboxColStyle>
            </Row>
          </Fragment>
        ))}
      </Card>
    </>
  );
};

UserRolesForm.defaultProps = {};

UserRolesForm.propTypes = {
  type: PropTypes.string.isRequired,
};

export default UserRolesForm;
